import { Link } from "react-router-dom";
import { AlertCircle, ChevronRight } from "lucide-react";
import useApprovalsCount from "../lib/useApprovalsCount";

/**
 * Red blinking banner shown at the top of dashboards when there are pending
 * approvals waiting for the current role.
 *
 * - Client (`role="client"`): show when issues + extras `pending_client > 0`.
 *   Links to `/cliente/aprovacoes`.
 * - Admin (`role="admin"`): show when ANY client has items waiting OR a
 *   client has decided in the last 24h. Links to `/admin/problemas`.
 *
 * Cheap polling every 20s via `useApprovalsCount`.
 */
export default function PendingApprovalsBanner({ role }) {
  const a = useApprovalsCount(role);
  if (role === "client" && (a.total || 0) === 0) return null;
  if (role === "admin" && (a.pending_total || 0) === 0
      && (a.decided_24h_total || 0) === 0) return null;

  const isClient = role === "client";
  const link = isClient ? "/cliente/aprovacoes" : "/admin/problemas";
  const total = isClient ? a.total : a.pending_total;
  const issues = a.pending_issues;
  const extras = a.pending_extras;

  // Admin "decided in last 24h, nothing pending" → amber (informational),
  // every other case (client pending OR admin pending) → red (urgent).
  const adminDecidedOnly = role === "admin" && a.pending_total === 0
                            && a.decided_24h_total > 0;
  const accent = adminDecidedOnly ? "#f59e0b" : "#ef4444";

  const parts = [];
  if (issues > 0) parts.push(`${issues} problema${issues === 1 ? "" : "s"}`);
  if (extras > 0) parts.push(`${extras} extra${extras === 1 ? "" : "s"}`);
  const summary = parts.length ? parts.join(" + ") : "0 pendentes";

  return (
    <Link to={link}
          className="lux-card p-5 flex items-center gap-4 transition-colors group"
          style={{
            borderColor: `${accent}99`,
            background: `${accent}0d`,
          }}
          data-testid={`pending-approvals-banner-${role}`}>
      <span className="relative shrink-0">
        {!adminDecidedOnly && (
          <span className="absolute inset-0 rounded-full animate-ping opacity-50"
                style={{ background: accent }} />
        )}
        <span className="relative w-12 h-12 rounded-full flex items-center justify-center text-white"
              style={{ background: accent }}>
          <AlertCircle size={22} />
        </span>
      </span>
      <div className="flex-1 min-w-0">
        <div className={`eyebrow mb-1 ${adminDecidedOnly ? "" : "animate-pulse"}`}
             style={{ color: accent }}>
          {isClient ? "A aguardar a sua decisão"
            : adminDecidedOnly ? "Decisões recentes"
            : "Pendente do cliente"}
        </div>
        <div className="font-display text-lg md:text-xl">
          {isClient ? (
            total === 1
              ? "Tem 1 item para aprovar"
              : `Tem ${total} itens para aprovar`
          ) : (
            <>
              {a.pending_total > 0
                ? <>{a.pending_total} {a.pending_total === 1 ? "pedido aguarda" : "pedidos aguardam"} aprovação do cliente</>
                : <>{a.decided_24h_total} {a.decided_24h_total === 1 ? "decisão" : "decisões"} nas últimas 24h</>}
            </>
          )}
        </div>
        <div className="text-xs text-[#a0b2af] mt-0.5">{summary}</div>
      </div>
      <ChevronRight size={20} className="shrink-0 group-hover:translate-x-1 transition-transform"
                    style={{ color: accent }} />
    </Link>
  );
}
